import dayjs from 'dayjs';

import { isStale } from '../manage/manage';

/**
 * The tag manager's model: what the screen shows (kept in the URL, so the way
 * back from a tag's people restores it), the counts on its tiles, and the
 * editor's checks. Pure, so it is tested without rendering.
 */

export interface ManagedTag {
  id: number;
  name: string;
  category: string | null;
  color: string | null;
  description: string | null;
  isSystem: boolean;
  contactCount: number;
  createdAt: string;
  updatedAt: string;
  lastAddedAt: string | null;
}

/** The tiles above the table, each a filter: every tag, then the ones worth a look. */
export type TagShow = 'all' | 'unused' | 'stale' | 'system';

export type TagSort = 'people' | 'name' | 'lastUsed' | 'newest';

export interface TagView {
  search: string;
  /** A category's name, NO_CATEGORY for tags without one, or null for every category. */
  category: string | null;
  show: TagShow;
  sort: TagSort;
}

/** Tags with no category, in the URL and in the category chips. */
export const NO_CATEGORY = '-';

export const DEFAULT_VIEW: TagView = { search: '', category: null, show: 'all', sort: 'people' };

const SHOWS: ReadonlyArray<TagShow> = ['all', 'unused', 'stale', 'system'];

export const SORTS: ReadonlyArray<{ id: TagSort; label: string }> = [
  { id: 'people', label: 'Most people' },
  { id: 'name', label: 'Name A–Z' },
  { id: 'lastUsed', label: 'Recently used' },
  { id: 'newest', label: 'Newest' },
];

/** Reads the view from the URL; anything unknown falls back to the default. */
export const parseTagView = (params: URLSearchParams): TagView => {
  const show = params.get('show') as TagShow;
  const sort = params.get('sort') as TagSort;
  return {
    search: (params.get('q') ?? '').slice(0, 100),
    category: params.get('category') || null,
    show: SHOWS.includes(show) ? show : 'all',
    sort: SORTS.some((s) => s.id === sort) ? sort : 'people',
  };
};

/** The URL form of a view, leaving out every default so the plain screen has a plain URL. */
export const tagViewParams = (view: TagView): URLSearchParams => {
  const params = new URLSearchParams();
  if (view.search) params.set('q', view.search);
  if (view.category) params.set('category', view.category);
  if (view.show !== 'all') params.set('show', view.show);
  if (view.sort !== 'people') params.set('sort', view.sort);
  return params;
};

const shows: Record<TagShow, (tag: ManagedTag, today: string) => boolean> = {
  all: () => true,
  unused: (tag) => tag.contactCount === 0,
  stale: (tag, today) => isStale(tag.lastAddedAt, today),
  system: (tag) => tag.isSystem,
};

/** How many tags each tile holds. */
export const tagStats = (tags: ReadonlyArray<ManagedTag>, today: string): Record<TagShow, number> => ({
  all: tags.length,
  unused: tags.filter((tag) => shows.unused(tag, today)).length,
  stale: tags.filter((tag) => shows.stale(tag, today)).length,
  system: tags.filter((tag) => shows.system(tag, today)).length,
});

/** Every category in use, by name, with how many tags are in it; tags without one last. */
export const categoriesOf = (tags: ReadonlyArray<ManagedTag>): Array<{ id: string; label: string; tags: number }> => {
  const counts = new Map<string, number>();
  tags.forEach((tag) => counts.set(tag.category ?? NO_CATEGORY, (counts.get(tag.category ?? NO_CATEGORY) ?? 0) + 1));
  const named = Array.from(counts.keys())
    .filter((id) => id !== NO_CATEGORY)
    .sort((a, b) => a.localeCompare(b))
    .map((id) => ({ id, label: id, tags: counts.get(id) ?? 0 }));
  const none = counts.get(NO_CATEGORY);
  return none ? [...named, { id: NO_CATEGORY, label: 'No category', tags: none }] : named;
};

const byName = (a: ManagedTag, b: ManagedTag) => a.name.localeCompare(b.name) || a.id - b.id;

const time = (value: string | null) => (value ? dayjs(value).valueOf() : -Infinity);

const ORDER: Record<TagSort, (a: ManagedTag, b: ManagedTag) => number> = {
  people: (a, b) => b.contactCount - a.contactCount || byName(a, b),
  name: byName,
  lastUsed: (a, b) => time(b.lastAddedAt) - time(a.lastAddedAt) || byName(a, b),
  newest: (a, b) => time(b.createdAt) - time(a.createdAt) || b.id - a.id,
};

/** The tags the view shows, in its order. Search matches the name, category and description. */
export const visibleTags = (tags: ReadonlyArray<ManagedTag>, view: TagView, today: string): Array<ManagedTag> => {
  const words = view.search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return tags
    .filter((tag) => shows[view.show](tag, today))
    .filter((tag) => !view.category || (tag.category ?? NO_CATEGORY) === view.category)
    .filter((tag) => {
      const text = [tag.name, tag.category, tag.description].filter(Boolean).join(' ').toLowerCase();
      return words.every((word) => text.includes(word));
    })
    .sort(ORDER[view.sort]);
};

/* ------------------------------------------------------------------ *
 * The editor
 * ------------------------------------------------------------------ */

/** The API's limits (crm-tags.ts in ilbp-backend). */
export const TAG_LIMITS = { name: 100, category: 50, description: 500 } as const;

/** Colours to pick from: the demo seed's, which NationBuilder's tags were given, and a few more. */
export const TAG_PALETTE: ReadonlyArray<string> = [
  '#2e7d32',
  '#1565c0',
  '#6a1b9a',
  '#ad1457',
  '#c62828',
  '#ef6c00',
  '#f9a825',
  '#00838f',
  '#4527a0',
  '#546e7a',
];

export interface TagForm {
  name: string;
  category: string;
  /** "" for no colour. */
  color: string;
  description: string;
}

export const EMPTY_TAG_FORM: TagForm = { name: '', category: '', color: '', description: '' };

export const formOf = (tag: ManagedTag): TagForm => ({
  name: tag.name,
  category: tag.category ?? '',
  color: tag.color ?? '',
  description: tag.description ?? '',
});

/** As the API stores it: trimmed, runs of spaces made one. */
const clean = (value: string) => value.trim().replace(/\s+/g, ' ');

/** A colour as the API takes it, `#1a82e2`, from what was typed; null when it is not one. */
export const normaliseColor = (value: string): string | null => {
  const hex = value.trim().toLowerCase().replace(/^#?/, '#');
  return /^#[0-9a-f]{6}$/.test(hex) ? hex : null;
};

/** What is wrong with the form, field by field, before anything is sent. */
export const checkTagForm = (form: TagForm): Partial<Record<keyof TagForm, string>> => {
  const problems: Partial<Record<keyof TagForm, string>> = {};
  const name = clean(form.name);
  if (!name) problems.name = 'A tag needs a name.';
  else if (name.length > TAG_LIMITS.name) problems.name = `At most ${TAG_LIMITS.name} characters.`;
  if (clean(form.category).length > TAG_LIMITS.category) problems.category = `At most ${TAG_LIMITS.category} characters.`;
  if (form.color.trim() && !normaliseColor(form.color)) problems.color = 'A colour is six hex digits, such as #1a82e2.';
  if (form.description.trim().length > TAG_LIMITS.description) {
    problems.description = `At most ${TAG_LIMITS.description} characters.`;
  }
  return problems;
};

/** A new tag's variables: empty optional fields left out. */
export const createVariables = (form: TagForm) => ({
  name: clean(form.name),
  ...(clean(form.category) ? { category: clean(form.category) } : {}),
  ...(normaliseColor(form.color) ? { color: normaliseColor(form.color) } : {}),
  ...(form.description.trim() ? { description: form.description.trim() } : {}),
});

/**
 * An edit's variables: only what changed, so an edit that leaves the name alone
 * never touches it (a system tag refuses any name sent that differs). An
 * emptied optional field is sent as "", which clears it.
 */
export const updateVariables = (tag: ManagedTag, form: TagForm) => {
  const next = createVariables(form);
  const changes: { name?: string; category?: string; color?: string; description?: string } = {};
  if (next.name !== tag.name) changes.name = next.name;
  if ((next.category ?? '') !== (tag.category ?? '')) changes.category = next.category ?? '';
  if ((next.color ?? '') !== (tag.color ?? '')) changes.color = next.color ?? '';
  if ((next.description ?? '') !== (tag.description ?? '')) changes.description = next.description ?? '';
  return changes;
};
